import { OmsProduct } from "../lusca-search-api/generated-src";
import type { OmsFilterDraft } from "./useOmsFilterCriteria";

const storageKey = (product: OmsProduct) => `oms-export-draft:${product}`;
const limit = 256 * 1024;

export function readOmsDraft(product: OmsProduct): OmsFilterDraft | undefined {
  try {
    const text = sessionStorage.getItem(storageKey(product));
    if (!text || text.length > limit) return undefined;
    const saved = JSON.parse(text) as {
      version?: number;
      draft?: OmsFilterDraft;
    };
    const draft = saved?.draft;
    if (
      saved?.version !== 1 ||
      !draft ||
      draft.report?.product !== product ||
      draft.snapshotId !== draft.report?.snapshotId ||
      !Array.isArray(draft.report?.columns) ||
      !draft.report.columns.every(
        (name: unknown) => typeof name === "string"
      ) ||
      !Array.isArray(draft.report?.parts) ||
      !Number.isFinite(Date.parse(draft.report.sourceRefreshedAt)) ||
      !Number.isFinite(draft.report.rowCount) ||
      !Number.isFinite(draft.report.totalBytes) ||
      !["full", "filtered"].includes(draft.tab) ||
      !["and", "or"].includes(draft.operation) ||
      typeof draft.editing !== "boolean" ||
      (draft.workspace !== undefined &&
        !["rows", "columns"].includes(draft.workspace)) ||
      (draft.presetEditor !== undefined &&
        (!draft.presetEditor ||
          !["export", "edit", "save", "manage"].includes(
            draft.presetEditor.view
          ) ||
          typeof draft.presetEditor.name !== "string" ||
          !["filters", "columns", "both"].includes(
            draft.presetEditor.scope
          ))) ||
      !Array.isArray(draft.criteria) ||
      draft.criteria.length > 20 ||
      !draft.criteria.every(
        (item: OmsFilterDraft["criteria"][number]) =>
          item &&
          Number.isInteger(item.id) &&
          typeof item.column === "string" &&
          typeof item.operator === "string" &&
          (item.value === undefined || typeof item.value === "string") &&
          (item.values === undefined ||
            (Array.isArray(item.values) &&
              item.values.every((value) => typeof value === "string")))
      ) ||
      !draft.columns ||
      !["all", "selected"].includes(draft.columns.strategy) ||
      !Array.isArray(draft.columns?.names) ||
      !draft.columns.names.every((name: unknown) => typeof name === "string")
    )
      return undefined;
    return draft;
  } catch {
    return undefined;
  }
}

export function writeOmsDraft(product: OmsProduct, draft: OmsFilterDraft) {
  try {
    const text = JSON.stringify({ version: 1, draft });
    if (text.length <= limit) sessionStorage.setItem(storageKey(product), text);
  } catch {
    // In-memory editing and accepted-job recovery still work without draft storage.
  }
}
